import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
// @ts-ignore - uuid types are available but not being detected
import { v4 as uuidv4 } from 'uuid';
import { StatItem, ServiceItem } from '@/contexts/ContentContext';
import PageHeader from '@/components/PageHeader';
import AdminPageContainer from '@/components/AdminPageContainer';
import HeroPreview from '@/components/Admin/HeroPreview';
import ServicesPreview from '@/components/Admin/ServicesPreview';
import StatsPreview from '@/components/Admin/StatsPreview';
import { useContent } from '@/contexts/ContentContext';
import { ContentSection } from './ContentSection';
import { HeroForm } from './HeroForm';
import { ServicesForm } from './ServicesForm';
import { StatsForm } from './StatsForm';

interface PreviewModes {
  [key: string]: boolean;
}

const getDefaultService = (): ServiceItem => ({
  id: uuidv4(),
  icon: 'FaTools',
  title: 'Novo Serviço',
  description: 'Descrição do serviço',
});

const getDefaultStat = (): StatItem => ({
  id: uuidv4(),
  number: 0,
  label: 'Novo Item',
  prefix: '',
  suffix: '+',
});

export function AdminContent() {
  const { sections, updateSection, saveChanges, isSaving, saveMessage } = useContent();
  const [previewModes, setPreviewModes] = useState<PreviewModes>({
    hero: false,
    services: false,
    stats: false,
  });

  const togglePreviewMode = useCallback((sectionId: string) => {
    setPreviewModes(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const getSection = useCallback((sectionId: string) => {
    return sections.find(s => s.id === sectionId);
  }, [sections]);

  const handleContentChange = useCallback((
    sectionId: string,
    field: string,
    value: any
  ) => {
    updateSection(sectionId, field, value);
  }, [updateSection]);

  const handleArrayItemChange = useCallback((
    sectionId: string,
    arrayField: string,
    index: number,
    field: string,
    value: any
  ) => {
    const section = getSection(sectionId);
    if (!section) return;

    const updatedArray = [...(section.content[arrayField] || [])];
    updatedArray[index] = { ...updatedArray[index], [field]: value };
    
    if (!updatedArray[index].id) {
      updatedArray[index].id = uuidv4();
    }
    
    updateSection(sectionId, arrayField, updatedArray);
  }, [getSection, updateSection]);

  const addArrayItem = useCallback((
    sectionId: string,
    arrayField: string,
    defaultValue: any
  ) => {
    const section = getSection(sectionId);
    if (!section) return;

    const currentArray = section.content[arrayField] || [];
    let newItem: any;
    
    if (arrayField === 'services') {
      newItem = { ...getDefaultService(), ...defaultValue };
    } else if (arrayField === 'stats') {
      newItem = { ...getDefaultStat(), ...defaultValue };
    } else {
      newItem = { ...defaultValue, id: uuidv4() };
    }
    
    updateSection(sectionId, arrayField, [...currentArray, newItem]);
  }, [getSection, updateSection]);

  const removeArrayItem = useCallback((
    sectionId: string,
    arrayField: string,
    index: number
  ) => {
    const section = getSection(sectionId);
    if (!section) return;

    const currentArray = [...(section.content[arrayField] || [])];
    currentArray.splice(index, 1);
    
    updateSection(sectionId, arrayField, currentArray);
  }, [getSection, updateSection]);

  const handleSave = async () => {
    try {
      await saveChanges();
    } catch (error) {
      console.error('Erro ao salvar conteúdo:', error);
    }
  };

  // Função auxiliar para renderizar o formulário de cada seção
  const renderForm = (sectionId: string) => {
    const section = getSection(sectionId);
    if (!section) return null;

    switch (sectionId) {
      case 'hero':
        return (
          <HeroForm
            content={section.content}
            onChange={(field, value) => handleContentChange(sectionId, field, value)}
          />
        );
      case 'services':
        return (
          <ServicesForm
            content={section.content}
            onChange={(field, value) => handleContentChange(sectionId, field, value)}
            onArrayItemChange={(...args) => handleArrayItemChange(sectionId, ...args)}
            onAddItem={addArrayItem.bind(null, sectionId)}
            onRemoveItem={removeArrayItem.bind(null, sectionId)}
          />
        );
      case 'stats':
        return (
          <StatsForm
            content={section.content}
            onChange={(field, value) => handleContentChange(sectionId, field, value)}
            onArrayItemChange={(...args) => handleArrayItemChange(sectionId, ...args)}
            onAddItem={addArrayItem.bind(null, sectionId)}
            onRemoveItem={removeArrayItem.bind(null, sectionId)}
          />
        );
      default:
        return null;
    }
  };

  // Função auxiliar para renderizar a pré-visualização de cada seção
  const renderPreview = (sectionId: string) => {
    const section = getSection(sectionId);
    if (!section) return null;

    switch (sectionId) {
      case 'hero':
        return <HeroPreview content={section.content} />;
      case 'services':
        return <ServicesPreview content={section.content} />;
      case 'stats':
        return <StatsPreview content={section.content} />;
      default:
        return null;
    }
  };

  // Seções disponíveis para edição
  const sectionsToEdit = ['hero', 'services', 'stats'];

  return (
    <AdminPageContainer>
      <div className="space-y-6">
        <PageHeader 
          title="Gerenciar Conteúdo" 
          onSave={handleSave} 
          isSaving={isSaving} 
        />

        {saveMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-md p-4 ${
              saveMessage.includes('sucesso')
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {saveMessage}
          </motion.div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {sectionsToEdit.map((sectionId) => {
            const section = getSection(sectionId);
            if (!section) return null;

            return (
              <ContentSection
                key={sectionId}
                section={section}
                previewMode={!!previewModes[sectionId]}
                onTogglePreview={() => togglePreviewMode(sectionId)}
                onContentChange={(field, value) => handleContentChange(sectionId, field, value)}
                onArrayItemChange={(...args) => handleArrayItemChange(sectionId, ...args)}
                onAddItem={addArrayItem.bind(null, sectionId)}
                onRemoveItem={removeArrayItem.bind(null, sectionId)}
                renderPreview={() => renderPreview(sectionId)}
                renderForm={() => renderForm(sectionId)}
              />
            );
          })}
        </div>
      </div>
    </AdminPageContainer>
  );
}

export default AdminContent;
